/**
 * Street End Sequencer for Poker Reborn
 *
 * Orchestrates the animation sequence when a betting round ends
 * and a new street begins (flop/turn/river).
 *
 * Steps:
 * 1. Collect all player pots → table center
 * 2. Flip community cards
 * 3. Start action timer (handled by server 'act' message)
 *
 * NOTE: SeatView → PlayerPotView chip animation is now handled immediately
 * when each bet is placed (see GameController.playBetToPlayerPotAnimation).
 *
 * @see docs/screens/room/PLAYER_POT.md - Player pot animations
 */

import { log } from '@/utils/log';
import { STREET_END_TIMING } from '@/config/animation';
import { tableState, type LastActionInfo } from '@/state/TableState';
import { targetRegistry } from '@/effects/registry/TargetRegistry';
import {
  ChipCollectToPotEffect,
  type ChipCollectToPotData,
  type PlayerPotSource,
} from '@/effects/library/chips/ChipCollectToPotEffect';
import { TablePotView } from '@/components/table/TablePotView';
import { audioServiceAdapter } from '@/effects/library/sound/AudioServiceAdapter';
import type { Card } from '@/types/game';

// =============================================================================
// TYPES
// =============================================================================

export interface StreetMessage {
  /** The street being dealt */
  street: 'flop' | 'turn' | 'river';
  /** The community cards for this street */
  cards: Card[];
}

// =============================================================================
// STREET END SEQUENCER CLASS
// =============================================================================

/**
 * Orchestrates the street-end animation sequence.
 *
 * This class manages the 5-step animation flow that happens when a
 * betting round ends and community cards are dealt.
 *
 * Usage:
 * ```typescript
 * const lastAction = getLastStreetAction();
 * streetEndSequencer.playSequence(
 *   { street: 'flop', cards: flopCards },
 *   lastAction
 * );
 * ```
 *
 * The sequence is fire-and-forget - callers should NOT await it.
 * Game state updates happen immediately; animations run independently.
 */
export class StreetEndSequencer {
  private static _instance: StreetEndSequencer | null = null;

  /** Track if a sequence is currently playing */
  private _isPlaying = false;

  /** Captured player pots at sequence start (to avoid race condition with new bets) */
  private _capturedPlayerPots: PlayerPotSource[] = [];

  // ---------------------------------------------------------------------------
  // Constructor & Singleton
  // ---------------------------------------------------------------------------

  private constructor() {}

  /**
   * Get the singleton instance.
   */
  static getInstance(): StreetEndSequencer {
    if (!StreetEndSequencer._instance) {
      StreetEndSequencer._instance = new StreetEndSequencer();
    }
    return StreetEndSequencer._instance;
  }

  /**
   * Reset the singleton instance (for testing).
   */
  static resetInstance(): void {
    StreetEndSequencer._instance = null;
  }

  // ---------------------------------------------------------------------------
  // Public API
  // ---------------------------------------------------------------------------

  /**
   * Check if a sequence is currently playing.
   */
  get isPlaying(): boolean {
    return this._isPlaying;
  }

  /**
   * Execute the 5-step street-end animation sequence.
   *
   * This is fire-and-forget: callers should NOT await this.
   * The promise is returned for testing purposes only.
   *
   * @param streetMessage - The flop/turn/river message with cards
   * @param lastAction - Info about the last player action (or null)
   * @returns Promise that resolves when the sequence completes
   */
  async playSequence(
    streetMessage: StreetMessage,
    _lastAction: LastActionInfo | null
  ): Promise<void> {
    if (this._isPlaying) {
      log.effects.warning`StreetEndSequencer: sequence already playing, skipping`;
      return;
    }

    this._isPlaying = true;
    const { street, cards } = streetMessage;

    // debugOverlay.log(`SEQUENCE START: ${street.toUpperCase()}`);
    log.effects.info`StreetEndSequencer: starting ${street} sequence`;

    try {
      // CRITICAL: Capture player pots IMMEDIATELY before any delays.
      // This prevents race condition where new bets accumulate during delays
      // and get cleared when collectToPot() runs.
      this._capturedPlayerPots = this.capturePlayerPots();
      const hasPlayerBets = this._capturedPlayerPots.length > 0;
      // debugOverlay.log(`hasPlayerBets = ${hasPlayerBets}, captured ${this._capturedPlayerPots.length} pots`);

      // FLOP-SPECIFIC: Hide table pot immediately at sequence start
      // State updates have already made it visible, but on FLOP the pot
      // should only appear when chips arrive (first appearance)
      if (street === 'flop') {
        const tablePotPos = targetRegistry.getTablePotPosition();
        const tablePotView = tablePotPos?.container instanceof TablePotView
          ? tablePotPos.container
          : null;
        if (tablePotView) {
          log.effects.debug`StreetEndSequencer: FLOP - hiding table pot at sequence start`;
          tablePotView.hideForAnimation();
        }
      }

      // NOTE: Step 1 (SeatView → PlayerPotView) is now handled immediately
      // when each bet is placed in GameController.playBetToPlayerPotAnimation()
      // So we skip directly to collecting player pots to table pot.

      // FLOP-2 / TURN-2 / RIVER-2: Wait for chips visibility (if bets exist)
      // This pause lets players see chips in player pots before collection
      // Note: On FLOP, hasPlayerBets is always true due to blinds (SB/BB)
      //       On TURN/RIVER, may be false if everyone checked
      if (hasPlayerBets) {
        log.effects.debug`StreetEndSequencer: ${street.toUpperCase()}-2 - visibility wait (${STREET_END_TIMING.chipsVisibleWait}ms)`;
        await this.delay(STREET_END_TIMING.chipsVisibleWait);

        // preCollectPause: configurable delay before chips fly to pot
        if (STREET_END_TIMING.preCollectPause > 0) {
          // debugOverlay.log(`preCollectPause START (${STREET_END_TIMING.preCollectPause}ms)`);
          await this.delay(STREET_END_TIMING.preCollectPause);
          // debugOverlay.log(`preCollectPause END`);
        }
      }

      // FLOP-3 / TURN-3 / RIVER-3: Collect to pot (if any player pots have chips)
      if (hasPlayerBets) {
        log.effects.debug`StreetEndSequencer: ${street.toUpperCase()}-3 - collect to pot`;
        await this.collectToPot(street);
      }

      // preCardsPause: configurable delay before cards deal (always runs)
      if (STREET_END_TIMING.preCardsPause > 0) {
        // debugOverlay.log(`preCardsPause START (${STREET_END_TIMING.preCardsPause}ms)`);
        await this.delay(STREET_END_TIMING.preCardsPause);
        // debugOverlay.log(`preCardsPause END`);
      }

      // FLOP-4 / TURN-4 / RIVER-4: Flip cards (always)
      log.effects.debug`StreetEndSequencer: ${street.toUpperCase()}-4 - flip ${cards.length} cards`;
      await this.flipCards(street, cards);

      // Step 3: Action timer starts via separate 'act' message
      // (handled by GameController, server provides +3s buffer)

      log.effects.info`StreetEndSequencer: ${street} sequence complete`;
    } catch (error) {
      log.effects.error`StreetEndSequencer: error during sequence: ${error}`;
    } finally {
      this._isPlaying = false;
    }
  }

  // ---------------------------------------------------------------------------
  // Step 1: Collect to Pot
  // ---------------------------------------------------------------------------

  /**
   * Animate all player pot chips flying to the table center pot.
   *
   * @param street - The street being dealt (flop/turn/river)
   *
   * On FLOP: Hide pot → animate → show pot when chips arrive (first appearance)
   * On TURN/RIVER: Keep pot visible → animate → pot updates via state
   *
   * IMPORTANT: Uses captured player pots from sequence start, not live state.
   * This prevents race condition where new bets accumulate during delays.
   */
  private async collectToPot(street: 'flop' | 'turn' | 'river'): Promise<void> {
    const particleLayer = targetRegistry.getParticleLayer();
    if (!particleLayer) {
      log.effects.warning`StreetEndSequencer: no particle layer for collect`;
      return;
    }

    const tablePotPos = targetRegistry.getTablePotPosition();
    if (!tablePotPos) {
      log.effects.warning`StreetEndSequencer: no table pot position`;
      return;
    }

    // Get TablePotView for visibility control
    const tablePotView = tablePotPos.container instanceof TablePotView
      ? tablePotPos.container
      : null;

    // Use captured player pots from sequence start (not live state!)
    // This prevents clearing new bets that accumulated during delays
    const playerPots = this._capturedPlayerPots;

    // On flop, pot appears for the first time - hide until chips arrive
    // On turn/river, pot already visible - keep it visible
    const isFirstCollection = street === 'flop';

    if (playerPots.length === 0) {
      log.effects.debug`StreetEndSequencer: no player pots to collect`;
      // Ensure pot shows if this is flop (state may have set value)
      if (isFirstCollection && tablePotView) {
        tablePotView.showAfterAnimation();
      }
      return;
    }

    if (isFirstCollection) {
      // Flop: hide pot, reveal when chips arrive
      tablePotView?.hideForAnimation();
    }

    return new Promise<void>((resolve) => {
      // Safety timeout to prevent hanging
      const timeout = setTimeout(() => {
        log.effects.warning`StreetEndSequencer: collect effect timed out`;
        if (isFirstCollection) {
          tablePotView?.showAfterAnimation();
        }
        resolve();
      }, 2000);

      const collectData: ChipCollectToPotData = {
        container: particleLayer,
        playerPots,
        toX: tablePotPos.x,
        toY: tablePotPos.y,
        potStagger: STREET_END_TIMING.collectStagger,
        onComplete: () => {
          clearTimeout(timeout);
          // Only need to show pot if we hid it (flop case)
          if (isFirstCollection) {
            tablePotView?.showAfterAnimation();
          }
          resolve();
        },
      };

      const effect = new ChipCollectToPotEffect(
        { type: 'pot' },
        collectData,
        { duration: STREET_END_TIMING.collectToPot }
      );

      // Play pot collection sound synchronized with animation
      void audioServiceAdapter.play('chips_collect');
      void effect.play();
    });
  }

  // ---------------------------------------------------------------------------
  // Step 2: Flip Cards
  // ---------------------------------------------------------------------------

  /**
   * Animate community cards flipping with synchronized sound.
   *
   * Step IDs:
   * - FLOP-4: 3 cards with 150ms stagger
   * - TURN-4: single card
   * - RIVER-4: single card
   *
   * Sound plays at the moment cards start flipping (not when event fires).
   * Cards should already be dealt face-down before calling this.
   *
   * @see docs/systems/SOUND_EVENTS.md - Sound synchronization
   */
  private async flipCards(
    street: 'flop' | 'turn' | 'river',
    _cards: Card[]
  ): Promise<void> {
    // Play street-specific sound synchronized with visual flip
    // flop uses 'flop' sounds, turn/river use 'turn' sounds
    const soundId = street === 'flop' ? 'flop' : 'turn';
    void audioServiceAdapter.play(soundId);
    log.effects.debug`StreetEndSequencer: playing ${soundId} sound for ${street}`;

    // Get CommunityCardsView from registry
    const communityCardsView = targetRegistry.getCommunityCardsView();

    if (communityCardsView) {
      // Use the animated flip method
      await communityCardsView.flipCardsAnimated(street);
    } else {
      // Fallback: just wait the expected duration (no animation)
      log.effects.warning`StreetEndSequencer: no communityCardsView, using timing fallback`;
      if (street === 'flop') {
        // Flop: 3 cards with stagger = 150 * 2 + 250 (CardView flip duration)
        const totalDuration =
          STREET_END_TIMING.cardStagger * 2 + STREET_END_TIMING.cardFlip;
        await this.delay(totalDuration);
      } else {
        // Turn/River: single card
        await this.delay(STREET_END_TIMING.cardFlip);
      }
    }
  }

  // ---------------------------------------------------------------------------
  // Helpers
  // ---------------------------------------------------------------------------

  /**
   * Capture current player pots as a snapshot.
   *
   * Called immediately at sequence start to capture the bets that exist
   * at that moment. This prevents the race condition where new bets
   * accumulate during delays and get incorrectly cleared.
   */
  private capturePlayerPots(): PlayerPotSource[] {
    const playerPots: PlayerPotSource[] = [];
    const players = tableState.players.get();

    players.forEach((player, seat) => {
      if (player.bet > 0) {
        const potPos = targetRegistry.getPlayerPotPosition(seat);
        if (potPos) {
          playerPots.push({
            x: potPos.x,
            y: potPos.y,
            amount: player.bet,
            seatNumber: seat,
          });
        }
      }
    });

    return playerPots;
  }

  /**
   * Check if any players have chips in their pot area.
   * @deprecated Use capturePlayerPots() and check length instead
   */
  private hasPlayerPotsWithChips(): boolean {
    const players = tableState.players.get();
    for (const [, player] of players) {
      if (player.bet > 0) {
        return true;
      }
    }
    return false;
  }

  /**
   * Delay helper.
   */
  private delay(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }
}

// =============================================================================
// SINGLETON EXPORT
// =============================================================================

/**
 * Global street end sequencer instance.
 */
export const streetEndSequencer = StreetEndSequencer.getInstance();
